import { useCallback, useEffect, useRef, useState } from "react";
import { useDialogFocus } from "../../../../hooks/useDialogFocus";
import {
  getProspectExports,
  getProspectExportDownloadUrl,
  type ProspectExport,
} from "../../../../services/prospects";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { Button } from "../../../../components/Button";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import "./prospectExports.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /**
   * What a new export would cover, in the same words the rows use — so the button says
   * what it is about to do rather than leaving the coach to infer it from the filters
   * they set several minutes ago.
   */
  filterSummary: string;
  /** Queues an export with the filters currently on screen. */
  onCreateExport: () => Promise<void>;
}

/** How often the list refreshes while something is still building. */
const POLL_MS = 3000;

const IN_PROGRESS = new Set(["Pending", "Running"]);

const formatBytes = (bytes: number): string => {
  if (bytes <= 0) return "—";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatWhen = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** What the export covered, so two taken minutes apart can be told apart. */
const describeFilters = (item: ProspectExport): string => {
  const parts: string[] = [];
  if (item.teamName) parts.push(item.teamName);
  if (item.searchTerm) parts.push(`“${item.searchTerm}”`);
  if (item.gradYears) parts.push(`Class of ${item.gradYears}`);
  return parts.length > 0 ? parts.join(" · ") : "All players";
};

const describeStatus = (item: ProspectExport): string => {
  switch (item.status) {
    case "Pending":
      return "Queued";
    case "Running":
      return "Preparing…";
    case "Failed":
      return item.errorMessage || "Failed";
    default:
      if (!item.isDownloadable) return "Expired";
      return `${item.prospectCount} players · ${item.pageCount} handwritten pages · ${formatBytes(item.fileSizeBytes)}`;
  }
};

/**
 * The coach's export archives.
 *
 * Exports are built in the background — collecting the handwritten pages takes longer than
 * a request can stay open — so there has to be somewhere they land. This is it: what was
 * asked for, whether it is ready, and a link while it lasts.
 */
export const ProspectExportsPanel = ({
  isOpen,
  onClose,
  filterSummary,
  onCreateExport,
}: Props) => {
  const [exports, setExports] = useState<ProspectExport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Read through a ref inside the interval so the poll doesn't have to be torn down and
  // rebuilt every time the list changes.
  const hasPendingRef = useRef(false);
  hasPendingRef.current = exports.some((item) => IN_PROGRESS.has(item.status));

  const load = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setIsLoading(true);
    try {
      setExports(await getProspectExports());
      setError(null);
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to load exports"));
    } finally {
      if (showSpinner) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    void load(true);
  }, [isOpen, load]);

  // Poll only while something is actually building, and only while the panel is open —
  // a background timer against a closed panel is just load with nobody watching.
  useEffect(() => {
    if (!isOpen) return;

    const timer = window.setInterval(() => {
      if (hasPendingRef.current) void load(false);
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [isOpen, load]);

  const handleCreate = async () => {
    if (isCreating) return;
    setIsCreating(true);
    setError(null);
    try {
      await onCreateExport();
      // Straight back to the list so the queued row appears under the button that made it.
      await load(false);
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to start the export"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDownload = async (item: ProspectExport) => {
    setDownloadingId(item.id);
    setError(null);
    try {
      const { url } = await getProspectExportDownloadUrl(item.id);
      // Straight to the signed URL: the bytes come from cloud storage, never back through
      // the API, which is the whole point of building it out of band.
      window.location.href = url;
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to start the download"));
      // The row's state is the likeliest reason it failed — expired, or not finished yet.
      void load(false);
    } finally {
      setDownloadingId(null);
    }
  };

  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogFocus({ isOpen, dialogRef, onClose });

  if (!isOpen) return null;

  return (
    <>
      <div className="prospect-exports-overlay" onClick={onClose} />
      <div ref={dialogRef} tabIndex={-1}
        className="prospect-exports-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Exports"
      >
        <div className="prospect-exports-panel__header">
          <div>
            <h2 className="prospect-exports-panel__title">Exports</h2>
            <p className="prospect-exports-panel__subtitle">
              Each one holds the roster CSV and the handwritten notes for the players it
              covers. Links last 7 days.
            </p>
          </div>
          <ModalCloseButton onClick={onClose} data-testid="exports-close" />
        </div>

        <div className="prospect-exports-panel__new">
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={isCreating}
            data-testid="create-export"
          >
            {isCreating ? "Starting…" : `New export — ${filterSummary}`}
          </Button>
        </div>

        {error && (
          <div className="prospect-exports-panel__error" role="alert">
            {error}
          </div>
        )}

        <div className="prospect-exports-panel__body">
          {isLoading ? (
            <p className="prospect-exports-panel__empty">Loading exports…</p>
          ) : exports.length === 0 ? (
            <p className="prospect-exports-panel__empty" data-testid="exports-empty">
              No exports yet.
            </p>
          ) : (
            <ul className="prospect-exports-panel__list">
              {exports.map((item) => (
                <li
                  key={item.id}
                  className="prospect-exports-panel__row"
                  data-testid={`export-row-${item.id}`}
                >
                  <div className="prospect-exports-panel__row-main">
                    <span className="prospect-exports-panel__row-title">
                      {describeFilters(item)}
                    </span>
                    <span className="prospect-exports-panel__row-meta">
                      {formatWhen(item.createdAt)} · {describeStatus(item)}
                    </span>
                    {item.omittedPageCount > 0 && (
                      <span className="prospect-exports-panel__row-warn">
                        {item.omittedPageCount} page
                        {item.omittedPageCount === 1 ? "" : "s"} could not be included — see
                        README.txt inside the zip
                      </span>
                    )}
                  </div>

                  {item.isDownloadable ? (
                    <Button
                      variant="secondary"
                      onClick={() => handleDownload(item)}
                      disabled={downloadingId === item.id}
                      data-testid={`export-download-${item.id}`}
                    >
                      {downloadingId === item.id ? "Opening…" : "Download"}
                    </Button>
                  ) : (
                    <span
                      className={`prospect-exports-panel__badge${
                        item.status === "Failed"
                          ? " prospect-exports-panel__badge--failed"
                          : ""
                      }`}
                    >
                      {IN_PROGRESS.has(item.status)
                        ? "Working"
                        : item.status === "Failed"
                          ? "Failed"
                          : "Expired"}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};
